import { useQuery } from '@tanstack/react-query';
import { getGroups, getUserList } from '../../../../api/users-api';
import type { LabelPermissionModel } from '../../../../api/drive-labels-api';

// Same page shape the people search (SharedEmailAutocomplete) sends.
const searchPage = () => ({
  cursor: null,
  results: [],
  nameSpace: '',
  firstPull: true,
  searchTime: 0,
  pageSize: 5,
});

const sameEmail = (a: string | undefined, b: string) =>
  !!a && a.trim().toLowerCase() === b.trim().toLowerCase();

/**
 * The display name for a label permission's person or group, found by email in gPanel's search
 * index (the same read-only search the people picker uses). It deliberately avoids the single-user
 * endpoint: that one cleans up the user's index entry and scheduled jobs when an email isn't a
 * user, which a group or outside email would trigger. No name found (or no index locally) means no
 * name; lookups are silent and never retried.
 */
export default function usePrincipalName(
  email: string,
  principalType: LabelPermissionModel['principalType']
) {
  const lookUpUser = !!email && principalType !== 'GROUP';

  const user = useQuery({
    queryKey: ['LabelPrincipalName', 'user', email],
    queryFn: () => getUserList(searchPage(), email, '', null, null),
    retry: false,
    enabled: lookUpUser,
  });
  const match = user.data?.results.find((result) =>
    sameEmail(result.userEmail, email)
  );
  const userName = match
    ? `${match.firstName ?? ''} ${match.lastName ?? ''}`.trim()
    : '';

  const groupNeeded =
    !!email && (!lookUpUser || ((user.isSuccess || user.isError) && !match));
  const group = useQuery({
    queryKey: ['LabelPrincipalName', 'group', email],
    queryFn: () => getGroups(searchPage(), email),
    retry: false,
    enabled: groupNeeded,
  });
  const groupName =
    group.data?.results.find((result) => sameEmail(result.email, email))
      ?.name ?? '';

  return userName || groupName;
}
